import {
  AES_GCM_NONCE_LENGTH_BYTES,
  AES_GCM_TAG_LENGTH_BYTES,
  decryptAes256Gcm,
  encryptAes256Gcm,
} from './aes_gcm.js';
import { base64ToBytes, bytesToBase64, randomBytes, toUint8Array, utf8ToBytes } from './bytes.js';

export const ENVELOPE_VERSION = 'amdmd-e1';
export const ENVELOPE_ALGORITHM = 'AES-256-GCM';

function requireString(value, fieldName) {
  if (typeof value !== 'string' || !value) {
    throw new Error(`${fieldName} is required.`);
  }
}

export function buildCanonicalAad({ clientId, objectType, objectId }) {
  requireString(clientId, 'clientId');
  requireString(objectType, 'objectType');
  requireString(objectId, 'objectId');

  return utf8ToBytes(
    `client_id=${clientId}|object_type=${objectType}|object_id=${objectId}|schema=${ENVELOPE_VERSION}`
  );
}

export function decodeEnvelope(envelope) {
  if (!envelope || typeof envelope !== 'object') {
    throw new Error('envelope must be an object.');
  }
  if (envelope.version !== ENVELOPE_VERSION) {
    throw new Error(`Unsupported envelope version: ${envelope.version}`);
  }
  if (envelope.alg !== ENVELOPE_ALGORITHM) {
    throw new Error(`Unsupported envelope algorithm: ${envelope.alg}`);
  }

  const nonceBytes = base64ToBytes(envelope.nonce_b64, 'nonce_b64');
  const aadBytes = base64ToBytes(envelope.aad_b64, 'aad_b64');
  const ciphertextBytes = base64ToBytes(envelope.ciphertext_b64 ?? '', 'ciphertext_b64');
  const tagBytes = base64ToBytes(envelope.tag_b64, 'tag_b64');

  if (nonceBytes.length !== AES_GCM_NONCE_LENGTH_BYTES) {
    throw new Error(`nonce_b64 must decode to ${AES_GCM_NONCE_LENGTH_BYTES} bytes.`);
  }
  if (tagBytes.length !== AES_GCM_TAG_LENGTH_BYTES) {
    throw new Error(`tag_b64 must decode to ${AES_GCM_TAG_LENGTH_BYTES} bytes.`);
  }
  if (aadBytes.length === 0) {
    throw new Error('aad_b64 must not be empty.');
  }

  return { nonceBytes, aadBytes, ciphertextBytes, tagBytes };
}

export function encodeEnvelope({ nonceBytes, aadBytes, ciphertextBytes, tagBytes }) {
  const nonce = toUint8Array(nonceBytes, 'nonceBytes');
  const aad = toUint8Array(aadBytes, 'aadBytes');
  const ciphertext = toUint8Array(ciphertextBytes, 'ciphertextBytes');
  const tag = toUint8Array(tagBytes, 'tagBytes');

  if (nonce.length !== AES_GCM_NONCE_LENGTH_BYTES) {
    throw new Error(`nonceBytes must be ${AES_GCM_NONCE_LENGTH_BYTES} bytes.`);
  }
  if (tag.length !== AES_GCM_TAG_LENGTH_BYTES) {
    throw new Error(`tagBytes must be ${AES_GCM_TAG_LENGTH_BYTES} bytes.`);
  }
  if (aad.length === 0) {
    throw new Error('aadBytes must not be empty.');
  }

  return {
    version: ENVELOPE_VERSION,
    alg: ENVELOPE_ALGORITHM,
    nonce_b64: bytesToBase64(nonce),
    aad_b64: bytesToBase64(aad),
    ciphertext_b64: bytesToBase64(ciphertext),
    tag_b64: bytesToBase64(tag),
  };
}

export async function encryptToEnvelope({
  keyBytes,
  plaintextBytes,
  aadBytes,
  nonceBytes = randomBytes(AES_GCM_NONCE_LENGTH_BYTES),
}) {
  const plaintext = toUint8Array(plaintextBytes, 'plaintextBytes');
  const aad = toUint8Array(aadBytes, 'aadBytes');
  const nonce = toUint8Array(nonceBytes, 'nonceBytes');

  if (aad.length === 0) {
    throw new Error('aadBytes must not be empty.');
  }

  const { ciphertextBytes, tagBytes } = await encryptAes256Gcm({
    keyBytes,
    plaintextBytes: plaintext,
    nonceBytes: nonce,
    aadBytes: aad,
  });

  return encodeEnvelope({
    nonceBytes: nonce,
    aadBytes: aad,
    ciphertextBytes,
    tagBytes,
  });
}

export async function decryptFromEnvelope({ keyBytes, envelope }) {
  const { nonceBytes, aadBytes, ciphertextBytes, tagBytes } = decodeEnvelope(envelope);
  return decryptAes256Gcm({
    keyBytes,
    nonceBytes,
    aadBytes,
    ciphertextBytes,
    tagBytes,
  });
}
